import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { ScreenWake } from "@/lib/capture.ts";
import { EngineConnection, type ConnectionState } from "@/lib/connection.ts";
import { PcmPlayer } from "@/lib/player.ts";
import { defaultLanguage } from "@/lib/listenerLanguage.ts";
import type { ReadyEvent } from "@/lib/protocol.ts";
import { applyEvent, emptyTranscript } from "@/lib/transcript.ts";
import { wsUrl } from "@/lib/urls.ts";

const LANG_KEY = "interligne.listener.lang";

function savedLanguage(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("lang");
    return fromUrl || localStorage.getItem(LANG_KEY);
  } catch {
    return null;
  }
}

function rememberLanguage(lang: string | null): void {
  try {
    if (lang) localStorage.setItem(LANG_KEY, lang);
  } catch {
    /* storage unavailable */
  }
}

export function useListener(code: string) {
  const [transcript, dispatch] = useReducer(applyEvent, undefined, emptyTranscript);
  const [link, setLink] = useState<ConnectionState>("connecting");
  const [session, setSession] = useState<ReadyEvent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lang, setLangState] = useState<string | null>(null);
  const [audioOn, setAudioOn] = useState(false);
  const [backlog, setBacklog] = useState(0);
  const [track, setTrack] = useState("main");
  const [delayS, setDelayS] = useState(3);
  const [realtimeOn, setRealtime] = useState(false);
  const realtime = useRef(false);

  const conn = useRef<EngineConnection | null>(null);
  const player = useRef(new PcmPlayer());
  const wake = useRef(new ScreenWake());
  const prefs = useRef<{ lang: string | null; audio: boolean }>({ lang: null, audio: false });
  const chosen = useRef(false);

  const sendPrefs = useCallback(() => {
    conn.current?.send({ type: "prefs", ...prefs.current });
  }, []);

  useEffect(() => {
    const connection = new EngineConnection({
      url: wsUrl(),
      first: () => ({ type: "listen", code, ...prefs.current }),
      resume: (info) => ({ type: "listen", code, sessionId: info.sessionId, lastSeq: info.lastSeq, ...prefs.current }),
      maxAttempts: 40,
      onAudio: (chunk) => player.current.push(chunk),
      onEvent: (event) => {
        dispatch(event);
        if (event.type === "ready") {
          setSession(event);
          setError(null);
          setTrack(event.track ?? "main");
          realtime.current = event.realtime === true;
          setRealtime(realtime.current);
          if (realtime.current) {
            player.current.setDelay(0);
            setDelayS(0);
          } else if (typeof event.audioDelayS === "number") {
            player.current.setDelay(event.audioDelayS);
            setDelayS(event.audioDelayS);
          }
          if (!chosen.current) {
            chosen.current = true;
            const pick = defaultLanguage(event, [
              savedLanguage(),
              ...navigator.languages.map((l) => l.slice(0, 2).toLowerCase()),
            ]);
            if (pick && pick !== prefs.current.lang) {
              prefs.current = { ...prefs.current, lang: pick };
              setLangState(pick);
              conn.current?.send({ type: "prefs", ...prefs.current });
            }
          }
        }
        if (event.type === "prefs") {
          setLangState(event.lang);
          setTrack(event.track ?? "main");
          if (prefs.current.audio && !event.audio) setAudioOn(false);
        }
        if (event.type === "audio_start") {
          player.current.sampleRate = event.sampleRate;
          // Buffered sessions place each sentence on the speaker's timeline;
          // real-time ones just play it now, right after what is already queued.
          if (!realtime.current && typeof event.speechStartS === "number" && typeof event.speechNowS === "number") {
            player.current.segmentStart(event.speechNowS - event.speechStartS);
          }
        }
        if (event.type === "settings" && !realtime.current) {
          player.current.setDelay(event.audioDelayS);
          setDelayS(event.audioDelayS);
        }
        if (event.type === "error" && event.fatal) setError(event.message);
      },
      onState: (state) => setLink(state),
    });
    conn.current = connection;
    connection.open();
    const player_ = player.current;
    const wake_ = wake.current;
    return () => {
      connection.close();
      void player_.stop();
      void wake_.disable();
    };
  }, [code]);

  useEffect(() => {
    if (!audioOn) return;
    const id = window.setInterval(() => setBacklog(player.current.backlog()), 500);
    return () => window.clearInterval(id);
  }, [audioOn]);

  const setLanguage = useCallback(
    (next: string) => {
      rememberLanguage(next);
      prefs.current = { ...prefs.current, lang: next };
      setLangState(next);
      sendPrefs();
    },
    [sendPrefs],
  );

  /** Call from a tap/click: browsers only allow sound after a user gesture. */
  const startAudio = useCallback(async () => {
    await player.current.unlock();
    prefs.current = { ...prefs.current, audio: true };
    setAudioOn(true);
    sendPrefs();
    void wake.current.enable();
  }, [sendPrefs]);

  const stopAudio = useCallback(async () => {
    prefs.current = { ...prefs.current, audio: false };
    setAudioOn(false);
    setBacklog(0);
    sendPrefs();
    await player.current.stop();
    await wake.current.disable();
  }, [sendPrefs]);

  return { transcript, link, session, error, lang, setLanguage, audioOn, startAudio, stopAudio, backlog, track, delayS, realtime: realtimeOn };
}
